import * as d3 from 'd3';
import { useMemo, useRef } from 'react';

import { TsPoint } from '../../MultiLineChart.types';

export interface MultiLineChartLinePlotProps {
  data: TsPoint[];
  xScale: d3.ScaleTime<number, number>;
  yScale: d3.ScaleLinear<number, number>;
  color: string;
  dashed?: boolean;
  tooltipDate?: Date;
}

const linePath =
  (
    xScale: d3.ScaleTime<number, number>,
    yScale: d3.ScaleLinear<number, number>
  ) =>
  (ts: TsPoint[]) =>
    d3
      .line<TsPoint>()
      .x(d => xScale(d.date))
      .y(d => yScale(d.value))
      .curve(d3.curveLinear)(ts);

export const MultiLineChartLinePlot = ({
  data,
  xScale,
  yScale,
  color,
  dashed,
  tooltipDate,
}: MultiLineChartLinePlotProps) => {
  const pathRef = useRef<SVGPathElement>(null);

  const path = useMemo(
    () => linePath(xScale, yScale)(data),
    [data, xScale, yScale]
  );

  const selectedData = data.find(
    d => d.date.valueOf() === tooltipDate?.valueOf()
  );

  const singlePoint = data.length === 1 ? data[0] : undefined;
  const optionalDashProp = dashed ? { strokeDasharray: '2' } : {};

  return (
    <g>
      <path
        ref={pathRef}
        data-testid="line-plot"
        vectorEffect="non-scaling-stroke"
        d={path ?? ''}
        stroke={color}
        fill="none"
        {...optionalDashProp}
      />
      {!!singlePoint && (
        <circle
          cx={xScale(singlePoint.date)}
          cy={yScale(singlePoint.value)}
          r="4"
          fill={color}
          data-testid="single-point"
        />
      )}
      {!!selectedData && (
        <circle
          cx={xScale(selectedData.date)}
          cy={yScale(selectedData.value)}
          r="4"
          fill={color}
          stroke="white"
          data-testid="selected-point"
        />
      )}
    </g>
  );
};
